/* eslint-disable max-lines-per-function */
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { useToast } from '@/hooks/use-toast';
import { useProfile } from '@/hooks/useProfile';
import { useCreateTermine } from '@/domain/termine/hooks/useCreateTermine';
import { FormValues } from '@/domain/termine/shared/types/CreateTermineInput';
import { dateMapper } from '@/domain/termine/features/CreateTermin/Sheet/utils/formMappers';
import { Form } from '@/components/ui/form';
import {
  markierungTypes,
  TerminWiederholung,
} from '@/domain/termine/shared/types';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { CreateTermineFormContext } from '@/domain/termine/features/CreateTermin/shared/CreateTermineFormContext';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormValidationSchema } from '@/domain/termine/features/CreateTermin/shared/FormValidationSchema';
import { useNavigate, useParams } from 'react-router-dom';

interface CreateTermineFormProviderProps {
  children: ReactNode;
}

const MOBILE_BREAKPOINT = 640;

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const result = reader.result?.toString();
        const base64 = result?.split(',')[1];
        if (!base64) {
          reject(new Error('Invalid file format'));
          return;
        }
        resolve(base64);
      } catch {
        reject(new Error('Failed to process file'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function formValuesToTerminInput(data: FormValues): FormValues {
  const mapped = dateMapper(data);

  return {
    titel: data.titel,
    beschreibung: data.beschreibung || '',
    datum: mapped.dateOnly,
    beginn: mapped.beginnDateTime || '',
    ende: mapped.endeDateTime || '',
    standortId: data.standortId,
    markierung: data.markierung,
    specials: data.specials || '',
    wiederholung: data.wiederholung || TerminWiederholung.OHNE,
    wiederholungBis: mapped.wiederholungBisTime,
    terminBild: data.terminBild,
    terminKalenderId: data.terminKalenderId,
  };
}

function getIsMobile(): boolean {
  if (typeof window === 'undefined') return true;
  return window.innerWidth < MOBILE_BREAKPOINT;
}

export function CreateTermineFormProvider({
  children,
}: CreateTermineFormProviderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { manager } = useProfile();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();

  const { toast } = useToast();
  const { createTermin, loading } = useCreateTermine();

  const { datum: datumParam } = useParams<{
    year: string;
    month: string;
    datum?: string;
  }>();

  const defaultDate = useMemo(
    () => (datumParam ? dayjs(datumParam).toDate() : dayjs().toDate()),
    [datumParam],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(FormValidationSchema()),
    defaultValues: {
      titel: '',
      beschreibung: '',
      datum: defaultDate.toISOString(),
      beginn: '',
      ende: '',
      standortId: standortId,
      markierung: markierungTypes[19],
      specials: '',
      wiederholung: TerminWiederholung.OHNE,
      wiederholungBis: undefined,
      terminBild: null,
      terminKalenderId: 'NO_KALENDER',
    },
    mode: 'onBlur',
  });

  const isMobile = useMemo(() => getIsMobile(), []);
  const isDesktop = !isMobile;

  const handleSubmit = form.handleSubmit(async (data) => {
    let terminBildInput: { name: string; base64Image: string } | undefined;
    if (data.terminBild?.file) {
      const base64Data = await fileToBase64(data.terminBild.file);
      terminBildInput = {
        name: data.terminBild.name,
        base64Image: base64Data,
      };
    }

    const terminInput = {
      ...formValuesToTerminInput(data),
      terminBildInput,
      standortId,
    };

    const response = await createTermin({
      managerId: manager.id,
      termin: terminInput,
    });

    if (response.error) {
      toast({
        title: t('domain.termine.sheet.messages.terminErstellt.error.title'),
        description:
          response.error.message ||
          t('domain.termine.sheet.messages.terminErstellt.error.description'),
        variant: 'destructive',
      });
    } else {
      toast({
        title: t('domain.termine.sheet.messages.terminErstellt.title'),
        description: t(
          'domain.termine.sheet.messages.terminErstellt.description',
        ),
        variant: 'default',
      });
      const terminDate = dayjs(data.datum);
      const terminYear = terminDate.format('YYYY');
      const terminMonth = terminDate.format('M');
      const terminDay = terminDate.format('D');
      navigate(`/termine/uebersicht/${terminYear}/${terminMonth}/${terminDay}`);
    }
  });

  const contextValue = useMemo(
    () => ({ loading, onSubmit: handleSubmit, form, isMobile, isDesktop }),
    [loading, handleSubmit, form, isMobile, isDesktop],
  );

  return (
    <CreateTermineFormContext.Provider value={contextValue}>
      <Form {...form}>{children}</Form>
    </CreateTermineFormContext.Provider>
  );
}
